import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import SVGPoint from '/@/components/map/assets/point';
import { useMyFields } from '/@/hooks/fields';
import { getSessionCountry } from '/@/store/session/selectors';

const FrostRiskMap = ({
    selectedMap,
    setMapLoading,
    mapLoading,
    zoomControl = true,
    fields = [],
    selectedField,
    handleSelectField,
}) => {
    // GMAP
    const [gmap, setGmap] = useState(undefined);
    const overlay = useRef(undefined);
    const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

    const mapRef = useRef();

    const country = useSelector(getSessionCountry);
    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,location,externalProvider',
    });

    const fieldsData = fields.length ? fields : fieldsQuery.data?.results;

    // Helper function to create marker content for AdvancedMarkerElement
    const createMarkerContent = (selected = false) => {
        const element = document.createElement('div');
        element.style.width = '35px';
        element.style.height = '35px';
        element.style.cursor = 'pointer';

        ReactDOM.render(React.createElement(SVGPoint, { selected }), element);

        return element;
    };

    useEffect(() => {
        if (mapRef.current && country) {
            const { bounds } = country;
            setGmap(
                new google.maps.Map(mapRef.current, {
                    mapTypeId: 'hybrid',
                    disableDefaultUI: true,
                    zoomControl,
                    mapId: 'DEMO_MAP_ID',
                    center: selectedField
                        ? { lat: selectedField.lat, lng: selectedField.lng }
                        : {
                              lat: bounds.south + (bounds.north - bounds.south) / 2,
                              lng: bounds.east + (bounds.west - bounds.east) / 2,
                          },
                    zoom: selectedField ? 16 : 4,
                })
            );
        }
    }, [country]);

    useEffect(() => {
        if (!gmap || (fieldsData || []).length === 0) return;

        // Remove old markers
        markersRef.current.forEach((m) => {
            m.map = null;
        });
        markersRef.current = [];

        // Add new markers
        fieldsData.forEach((field) => {
            const isSelected = selectedField && field.id === selectedField.id;
            const marker = new google.maps.marker.AdvancedMarkerElement({
                map: gmap,
                position: { lat: field.lat, lng: field.lng },
                content: createMarkerContent(isSelected),
                title: field.name,
            });
            markersRef.current.push(marker);
            marker.addListener('click', (ev) => {
                ev?.domEvent?.preventDefault();
                if (handleSelectField) {
                    handleSelectField(field);
                }
            });
        });
    }, [gmap, fieldsData, selectedField]);

    useEffect(() => {
        if (!gmap || !selectedMap) return;

        if (overlay.current) overlay.current.setMap(null);
        overlay.current = new google.maps.Data({
            map: gmap,
            style: (feature) => ({
                fillColor: feature.getProperty('fill'),
                fillOpacity: 0.7,
                strokeWeight: 0,
            }),
        });

        if (selectedMap.file) {
            setMapLoading((prevIsLoading) => ({
                ...prevIsLoading,
                [selectedMap.tabIndex]: true,
            }));
            loadOverlay('file', selectedMap.file, selectedMap.bounds);
        } else if (selectedMap.geojson) {
            loadOverlay('geojson', selectedMap.geojson, selectedMap.bounds);
        }
        if (!gmap || !selectedField) return;
        const { lat, lng } = selectedField;
        if (!lat || !lng) return;
        gmap.panTo({ lat, lng });
        gmap.setZoom(16);
    }, [selectedMap, gmap]);

    /**
     * @param {'file'|'geojson'} type
     * @param {*} geoJson
     * @param {{ north: number, south: number, east: number, west: number }} bounds
     */
    const loadOverlay = (type = 'file', geoJson, bounds) => {
        if (type === 'file') {
            overlay.current.loadGeoJson(geoJson, null, () => {
                setMapLoading((prevIsLoading) => ({
                    ...prevIsLoading,
                    [selectedMap.tabIndex]: false,
                }));
                fitBounds(bounds);
            });
        } else {
            overlay.current.addGeoJson(geoJson, null);
            fitBounds(bounds);
        }
    };

    useEffect(() => {
        if (!gmap || !selectedField) return;
        const { lat, lng } = selectedField;
        if (!lat || !lng) return;
        gmap.panTo({ lat, lng });
        gmap.setZoom(16);
    }, [gmap, selectedField]);
    /**
     *
     * @param {{north: number, south: number, east: number, west: number}} bounds
     * @returns
     */
    const fitBounds = (bounds) => {
        if (!bounds.north || !bounds.south || !bounds.east || !bounds.west) return;
        if (!gmap || selectedField) return;
        gmap.fitBounds(bounds, 3);
    };

    return (
        <Box
            ref={mapRef}
            sx={[
                {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                },
                mapLoading && { opacity: 0.7 },
            ]}
        />
    );
};

export default FrostRiskMap;
